'use client';

import { cn } from '@workspace/ui/lib/utils';
import { IUITile } from '@/src/types/ITile';
import { useToast } from '@/src/components/ui';
import { useEditableTextarea } from '@/src/hooks/use-editable-textarea';
import { useEditingHoverControl } from '../../../hooks/useEditingHoverControl';
import { useProfileUIStore } from '../../../store/index';
import { createTile, editTile } from '@/src/actions/tiles';
import { useClickOrDrag } from '../../../hooks/useClickOrDrag';
import { getTileEditStatus } from '@/src/utils/helpers';
import { Input } from '@workspace/ui/components/input';
import { useTilesStore } from '../../../store/tiles-store';
import { LoadingOverlay } from '../LoadingOverlay';

type Props = {
  isMyProfile?: boolean;
  tile: IUITile;
  touchedTileId: string | null;
  setTouchedTileId: (id: string | null) => void;
  onHoverStateChange?: (shouldDisable: boolean) => void;
};

export const TitleTile = ({
  isMyProfile = true,
  onHoverStateChange,
  tile,
  touchedTileId,
  setTouchedTileId,
}: Props) => {
  const { handleStartEditing, handleStopEditing } =
    useEditingHoverControl(onHoverStateChange);
  const updateTile = useTilesStore((state) => state.updateTile);
  const isMobileView = useProfileUIStore((state) => state.isMobileView);
  const previewMode = useProfileUIStore((state) => state.previewMode);
  const setTileLoading = useTilesStore((state) => state.setTileLoading);
  const toast = useToast();
  const { onMouseDown, onTouchStart } = useClickOrDrag(() => {
    handleStartEdit();
  });

  const {
    tempValue,
    value,
    isEditing,
    inputRef,
    handleChange,
    handleKeyDown,
    submitEdit,
    startEdit,
  } = useEditableTextarea({
    initialValue: tile.content || '',
    onSubmit: async (tempValue) => {
      const { wasEdited, hasContent, isEmpty } = getTileEditStatus(
        tile.content ? tile.content : '',
        tempValue
      );

      if (!isEmpty && wasEdited) {
        setTileLoading(tile.i, true);
        try {
          let response;
          if (!hasContent) {
            toast.updating('Adding title');
            const { iconName, ...tileWithoutIconName } = tile;

            response = await createTile({
              ...tileWithoutIconName,
              content: tempValue,
              title: '',
            });
            if (!response) {
              toast.error('Failed to create title');
              return;
            }
          } else {
            toast.updating('Updating title');
            response = await editTile(
              { content: tempValue, title: '' },
              tile.id as number
            );
            if (!response) {
              toast.error('Failed to update title');
              return;
            }
          }
          updateTile(tile.i, {
            ...tile,
            ...response,
            id: response.id,
            static: false,
          });

          toast.updated('Title saved');
        } catch (error) {
          toast.error('Failed to save Title');
        } finally {
          setTileLoading(tile.i, false);
        }
        handleStopEditing();
      }
    },
  });

  const handleStartEdit = () => {
    if (previewMode) return;
    startEdit();
    handleStartEditing();
  };

  const handleSubmitEdit = () => {
    submitEdit();
    handleStopEditing();
    setTouchedTileId(null);
  };

  if (isMyProfile && isEditing) {
    return (
      <div className="flex flex-col items-center justify-between w-full h-fit transition-colors cursor-pointer text-gray-text">
        {tile.isLoading && <LoadingOverlay />}
        <Input
          style={{
            border: 'none',
            outline: 'none',
            boxShadow: 'none',
            resize: 'none',
          }}
          ref={inputRef as React.RefObject<HTMLInputElement>}
          maxLength={30}
          value={tempValue}
          onChange={handleChange}
          onBlur={handleSubmitEdit}
          onKeyDown={handleKeyDown}
          className={cn(
            'w-full h-full text-black placeholder:text-gray-text bg-bg-gray placeholder:font-semibold placeholder:text-lg p-2 md:placeholder:text-2xl md:text-2xl text-lg font-semibold'
          )}
          placeholder="Add title..."
        />
      </div>
    );
  }

  if (tile.static && !isEditing) {
    return (
      <div
        className={cn('p-4 text-lg font-semibold h-fit w-full text-start', {
          'md:text-2xl': !isMobileView,
        })}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleStartEdit();
        }}
      >
        Add a title...
      </div>
    );
  }

  return (
    <h1
      className={cn(
        'p-2 w-full h-full overflow-y-auto placeholder:font-semibold! placeholder:text-lg! md:text-lg! text-lg! font-semibold',
        {
          'md:py-2 md:px-4 md:placeholder:text-2xl! md:text-2xl!': !isMobileView,
        }
      )}
      onMouseDown={onMouseDown}
      onTouchStart={(e) => {
        if (isMyProfile) {
          if (touchedTileId !== tile.i) {
            e.preventDefault();
            return;
          }
          onTouchStart(e);
        }
      }}
    >
      {tile.content}
    </h1>
  );
};
